import { Button, Modal } from 'lego-on-react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { __RouterContext } from 'react-router';

import i18n from '@yandex-int/i18n';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import * as keyset from 'i18n/IntentsPage';
import block from 'propmods';

import { granetApi } from '../../api';
import { CurrentAliceAppContext } from '../../context/aliceAppContext';
import { GrammarPageContext, useGrammarPageContext } from '../../context/grammarPageContext';
import { SettingsPageContext, useSettingsPageContext } from '../../context/settingsPageContext';
import { useLoadingData } from '../../hooks/useLoadingData';
import { CheckIcon } from '../../icons/check';
import { InfoIcon } from '../../icons/Info';
import { emptyGrammarIntentSettings, GrammarIntentShort, IntentStatus } from '../../model/grammar';
import { preventEsc } from '../../utils/events';
import { Handles as ConfirmHandles, UnmanagedConfirm } from '../Confirm/UnmanagedConfirm';
import { GrammarPageFlagsSettings } from '../GrammarPageFlagsSettings/GrammarPageFlagsSettings';
import { GrammarPageIntentModule } from '../GrammarPageIntentModule/GrammarPageIntentModule';
import { HintPopup } from '../HintPopup/HintPopup';
import { Loading } from '../Loading/Loading';

import './GrammarPageIntent.scss';

const b = block('GrammarPageIntent');
const t = i18n(keyset);

interface GrammarPageIntentPopupProps {
    onClose: (e?: Event) => void;
    intentId: string;
}

const GrammarPageIntentPopup: React.FC<GrammarPageIntentPopupProps> = ({ onClose, intentId }) => {
    const skillContext = useContext(CurrentAliceAppContext)!;
    const grammarPageContext = useGrammarPageContext();
    const settingsPageContext = useSettingsPageContext();
    const routerContext = useContext(__RouterContext);
    const confirmRef = useRef<ConfirmHandles>(null);
    const [isChanged, setIsChanged] = useState(false);
    const [intentSettings, setIntentSettings] = useState(emptyGrammarIntentSettings);

    const { removeIntentFromView } = grammarPageContext;
    const { isDraft, isEditable } = settingsPageContext;

    const onIntentDelete = () => {
        removeIntentFromView(intentId);
        onClose();
    };

    const { loadingMeta } = useLoadingData({
        loader: async () => {
            const settings = await granetApi.getGrammarIntent({
                skillId: skillContext.aliceApp.id,
                channel: skillContext.aliceApp?.channel,
                intentId: intentId,
                isDraft,
            });

            setIntentSettings(settings);
        },
    });
    const { state } = loadingMeta;

    const isLoading = state === 'loading';

    const skill = skillContext.aliceApp!;
    const customEntities = isDraft ? skill.draft.customEntities : skill.customEntities;

    const customEntitiesNames = (customEntities.match(/^entity\s+\w+(?=:)/) ?? []).map((match) =>
        match.replace(/^entity\s*/, ''),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onModalClose = useCallback(
        preventEsc((e) => {
            isChanged
                ? confirmRef.current?.open({
                      title: t('warning-title'),
                      description: t('warning-message'),
                      onConfirm: () => onClose(e),
                  })
                : onClose(e);
        }),
        [isChanged],
    );

    return (
        <Modal
            mix={{ block: 'GrammarPageIntent', elem: 'modal' }}
            autoclosable
            visible
            directions={['bottom-left']}
            onOutsideClick={onModalClose}
        >
            <div {...b('popup')}>
                {isLoading ? (
                    <Loading centered grow />
                ) : (
                    // Переопределение контекста нужно, так как в попапе от лего он теряется. Скорее всего с новыми версиями это исправлено
                    <GrammarPageContext.Provider value={grammarPageContext}>
                        <SettingsPageContext.Provider value={settingsPageContext}>
                            <CurrentAliceAppContext.Provider value={skillContext}>
                                <__RouterContext.Provider value={routerContext}>
                                    <GrammarPageIntentModule
                                        intentId={intentId}
                                        onDelete={onIntentDelete}
                                        readOnly={!isEditable}
                                        onClose={onModalClose}
                                        customEntitiesNames={customEntitiesNames}
                                        isChanged={isChanged}
                                        setIsChanged={setIsChanged}
                                        intentSettings={intentSettings}
                                        skillId={skillContext.aliceApp.id}
                                        entities={customEntities}
                                    />
                                </__RouterContext.Provider>
                            </CurrentAliceAppContext.Provider>
                        </SettingsPageContext.Provider>
                    </GrammarPageContext.Provider>
                )}
            </div>
            <UnmanagedConfirm ref={confirmRef} />
        </Modal>
    );
};

interface Props {
    intent: GrammarIntentShort;
}

const getHint = (status: IntentStatus) => {
    switch (status) {
        case IntentStatus.INVALID_GRAMMAR:
            return (
                <HintPopup hint={<>{t('tooltip-invalid-intent')}</>} when="hover">
                    <InfoIcon color="#f0ad4e" type="filled" />
                </HintPopup>
            );
        case IntentStatus.NEW:
            return (
                <HintPopup hint={t('tooltip-new')} when="hover">
                    <InfoIcon color="rgb(98, 131, 230)" type="filled" />
                </HintPopup>
            );
        case IntentStatus.OK:
            return <CheckIcon color="#6cba68" size="s" type="filled" />;
    }
};

export const GrammarPageIntent: React.FC<Props> = ({ intent }) => {
    const [popupIsVisible, setPopupIsVisible] = useState(false);
    const { status } = intent;

    const { isDraft } = useSettingsPageContext();

    useEffect(() => {
        if (popupIsVisible) {
            disableBodyScroll(document.body, { reserveScrollBarGap: true });
        } else {
            enableBodyScroll(document.body);
        }
    }, [popupIsVisible]);

    const openPopup = () => setPopupIsVisible(true);

    const displayFlags = __appConfig.user.isAdmin || Boolean(__appConfig.user.featureFlags.allowUseIntentFlags);
    const allowEditFlags =
        __appConfig.user.isAdmin || (Boolean(__appConfig.user.featureFlags.allowUseIntentFlags) && isDraft);

    return (
        <div {...b()}>
            <div {...b('wrapper')}>
                <Button
                    onClick={openPopup}
                    theme="normal"
                    size="m"
                    {...{ width: 'max' }}
                    cls={b('button-intent').className}
                >
                    {intent.humanReadableName || t('intent-no-name')}
                </Button>
                <div {...b('status')}>{getHint(status)}</div>
            </div>

            {displayFlags && (
                <div {...b('flags')}>
                    <GrammarPageFlagsSettings intent={intent} disabled={!allowEditFlags} />
                </div>
            )}
            {popupIsVisible && <GrammarPageIntentPopup onClose={() => setPopupIsVisible(false)} intentId={intent.id} />}
        </div>
    );
};
